// This file is auto-generated by @hey-api/openapi-ts

import type { Options as ClientOptions, TDataShape, Client } from './client'
import type {
  GetRootData,
  GetRootResponses,
  GetSystemOverviewData,
  GetSystemOverviewResponses,
  GetCameraInterfacesData,
  GetCameraInterfacesResponses,
  ApplyCameraInterfacesData,
  ApplyCameraInterfacesResponses,
  GetSystemLogsData,
  GetSystemLogsResponses,
  GetCitysenseCarLogsData,
  GetCitysenseCarLogsResponses,
  DownloadDebugLogData,
  DownloadDebugLogResponses,
  DownloadErrorLogData,
  DownloadErrorLogResponses,
  StartCitysenseServicesData,
  StartCitysenseServicesResponses,
  StopCitysenseServicesData,
  StopCitysenseServicesResponses,
  StartCalibrationData,
  StartCalibrationResponses,
  StartCalibrationErrors,
  GetCalibrationStatusData,
  GetCalibrationStatusResponses,
  GetCalibrationPreviewData,
  GetCalibrationPreviewResponses,
  GetCalibrationPreviewErrors,
  SaveCalibrationData,
  SaveCalibrationResponses,
  SaveCalibrationErrors,
  AbortCalibrationData,
  AbortCalibrationResponses,
  AbortCalibrationErrors,
  GetCalibrationLogData,
  GetCalibrationLogResponses,
  GetCalibrationFilesData,
  GetCalibrationFilesResponses,
} from './types.gen'
import { client as _heyApiClient } from './@tanstack/initializer'

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = ClientOptions<
  TData,
  ThrowOnError
> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>
}

/**
 * Service description
 * Returns the service name and the list of available endpoints.
 */
export const getRoot = <ThrowOnError extends boolean = false>(options?: Options<GetRootData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).get<GetRootResponses, unknown, ThrowOnError>({
    url: '/',
    ...options,
  })
}

/**
 * Aggregated system health overview
 * Evaluates all system predicates (services, network, GPS, TPU, storage, cameras, temperatures) and aggregates their results. Predicate results are cached per predicate; predicates whose dependencies fail are reported as skipped.
 */
export const getSystemOverview = <ThrowOnError extends boolean = false>(
  options?: Options<GetSystemOverviewData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetSystemOverviewResponses, unknown, ThrowOnError>({
    url: '/system-overview',
    ...options,
  })
}

/**
 * Propose camera interface names
 * Lists cameras via Pylon inside the citysense-car container, matches them to physical NICs through ARP neighbours and proposes the expected interface name for each camera. Never modifies the system.
 */
export const getCameraInterfaces = <ThrowOnError extends boolean = false>(
  options?: Options<GetCameraInterfacesData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetCameraInterfacesResponses, unknown, ThrowOnError>({
    url: '/camera-interfaces',
    ...options,
  })
}

/**
 * Apply proposed camera interface names
 * Recomputes the proposal and renames the matched NICs using apply-camera-interface-names.sh. On success, schedules citysense-update-connections to restart after a short delay. Refuses to apply when conflicts are present or when there is nothing to apply.
 */
export const applyCameraInterfaces = <ThrowOnError extends boolean = false>(
  options?: Options<ApplyCameraInterfacesData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<ApplyCameraInterfacesResponses, unknown, ThrowOnError>({
    url: '/camera-interfaces/apply',
    ...options,
  })
}

/**
 * Tail of the full systemd journal
 * Last 250 lines of journalctl --no-pager (all units including kernel).
 */
export const getSystemLogs = <ThrowOnError extends boolean = false>(
  options?: Options<GetSystemLogsData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetSystemLogsResponses, unknown, ThrowOnError>({
    url: '/logs/system',
    ...options,
  })
}

/**
 * Tail of citysense-car journal
 * Last 250 lines of journalctl -u citysense-car --no-pager.
 */
export const getCitysenseCarLogs = <ThrowOnError extends boolean = false>(
  options?: Options<GetCitysenseCarLogsData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetCitysenseCarLogsResponses, unknown, ThrowOnError>({
    url: '/logs/citysense-car',
    ...options,
  })
}

/**
 * Download the full debug log
 * Streams debug.jsonl as an attachment. Returns an empty body when no log directory or file is found.
 */
export const downloadDebugLog = <ThrowOnError extends boolean = false>(
  options?: Options<DownloadDebugLogData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<DownloadDebugLogResponses, unknown, ThrowOnError>({
    url: '/logs/debug.jsonl',
    ...options,
  })
}

/**
 * Download the full error log
 * Streams error.jsonl as an attachment. Returns an empty body when no log directory or file is found.
 */
export const downloadErrorLog = <ThrowOnError extends boolean = false>(
  options?: Options<DownloadErrorLogData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<DownloadErrorLogResponses, unknown, ThrowOnError>({
    url: '/logs/error.jsonl',
    ...options,
  })
}

/**
 * Start CitySense services
 * Starts the citysense-orchestrator systemd unit.
 */
export const startCitysenseServices = <ThrowOnError extends boolean = false>(
  options?: Options<StartCitysenseServicesData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<StartCitysenseServicesResponses, unknown, ThrowOnError>({
    url: '/start-citysense-services',
    ...options,
  })
}

/**
 * Stop CitySense services
 * Stops the citysense-orchestrator unit and, on success, also citysense-car and citysense-sync.
 */
export const stopCitysenseServices = <ThrowOnError extends boolean = false>(
  options?: Options<StopCitysenseServicesData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<StopCitysenseServicesResponses, unknown, ThrowOnError>({
    url: '/stop-citysense-services',
    ...options,
  })
}

/**
 * Start a headless calibration session
 * Stops CitySense services, starts the headless calibrator for a single side (FL or FR) and returns immediately. Poll /calibration/status and /calibration/preview.jpg while the session runs. One successful save completes the session. Returns 409 when a session is already active.
 */
export const startCalibration = <ThrowOnError extends boolean = false>(
  options: Options<StartCalibrationData, ThrowOnError>
) => {
  return (options.client ?? _heyApiClient).post<StartCalibrationResponses, StartCalibrationErrors, ThrowOnError>({
    url: '/calibration/start',
    ...options,
  })
}

/**
 * Calibration session status
 * Aggregates process state with the calibrator's state.json (position, success, seq).
 */
export const getCalibrationStatus = <ThrowOnError extends boolean = false>(
  options?: Options<GetCalibrationStatusData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetCalibrationStatusResponses, unknown, ThrowOnError>({
    url: '/calibration/status',
    ...options,
  })
}

/**
 * Latest calibration preview frame
 * JPEG overlay produced by the headless calibrator. Poll about once per second. Returns 404 until the first frame is available.
 */
export const getCalibrationPreview = <ThrowOnError extends boolean = false>(
  options?: Options<GetCalibrationPreviewData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<
    GetCalibrationPreviewResponses,
    GetCalibrationPreviewErrors,
    ThrowOnError
  >({
    url: '/calibration/preview.jpg',
    ...options,
  })
}

/**
 * Save the current side calibration
 * Writes a save command for the calibrator. Only accepted while the session is running and status.success is true. One successful save completes the session for the started side.
 */
export const saveCalibration = <ThrowOnError extends boolean = false>(
  options?: Options<SaveCalibrationData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<SaveCalibrationResponses, SaveCalibrationErrors, ThrowOnError>({
    url: '/calibration/save',
    ...options,
  })
}

/**
 * Abort the calibration session
 * Sends quit to the calibrator and, if it does not exit within a short grace period, kills the calibration container. The run script restarts the orchestrator on exit.
 */
export const abortCalibration = <ThrowOnError extends boolean = false>(
  options?: Options<AbortCalibrationData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<AbortCalibrationResponses, AbortCalibrationErrors, ThrowOnError>({
    url: '/calibration/abort',
    ...options,
  })
}

/**
 * Calibration run log
 * Plain-text stdout/stderr of run-calibration.sh --headless for the current or last session.
 */
export const getCalibrationLog = <ThrowOnError extends boolean = false>(
  options?: Options<GetCalibrationLogData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetCalibrationLogResponses, unknown, ThrowOnError>({
    url: '/calibration/log',
    ...options,
  })
}

/**
 * Installed calibration files
 * Existence and mtime of /etc/citysense/calibration-FL.json and calibration-FR.json.
 */
export const getCalibrationFiles = <ThrowOnError extends boolean = false>(
  options?: Options<GetCalibrationFilesData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetCalibrationFilesResponses, unknown, ThrowOnError>({
    url: '/calibration/files',
    ...options,
  })
}
